import React, { useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Typography,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { FaqItem } from '../../store/slices/faqsSlice';

export interface FaqDialogForm {
  sectionSelect: string;
  newSection: string; // make required to satisfy resolver inference
  question: string;
  answer: string;
}

interface FaqDialogProps {
  open: boolean;
  onClose: () => void;
  mode: 'add' | 'edit';
  faq?: FaqItem | null;
  sections: string[]; // existing sections to choose from
  onSave: (data: { section: string; question: string; answer: string }) => void;
}

// const contentMustContainRealText = /[A-Za-z0-9ก-๙]/;

const schema = yup.object({
  sectionSelect: yup
    .string()
    .required('Section is required')
    .test('not-empty-or-spaces', 'Section cannot be empty or only spaces', value => !!value && value.trim().length > 0)
    .test('valid-section', 'Please select a section or add a new one', function(value) {
      if (String(value) === '__new') {
        return !!this.parent.newSection && this.parent.newSection.trim().length > 0;
      }
      return true;
    }),
  newSection: yup
    .string()
    .default('')
    .when('sectionSelect', (sectionSelect, schema) => {
      if (String(sectionSelect) === '__new') {
        return schema
          .required('New section name is required')
          .test('not-empty-or-spaces', 'Section name cannot be empty or only spaces', value => !!value && value.replace(/\s/g, '').length > 0)
          .min(3, 'Section name must be at least 3 characters')
          .max(100, 'Section name must be at most 100 characters');
      }
      return schema;
    }),
  question: yup
    .string()
    .required('Question is required')
    .test('not-empty-or-spaces', 'Question cannot be empty or only spaces', value => !!value && value.replace(/\s/g, '').length > 0)
    .min(10, 'Question must be at least 10 characters')
    .max(500, 'Question must be at most 500 characters'),
  answer: yup
    .string()
    .required('Answer is required')
    .test('not-empty-or-spaces', 'Answer cannot be empty or only spaces', value => !!value && value.replace(/\s/g, '').length > 0)
    .min(10, 'Answer must be at least 10 characters')
    .max(2000, 'Answer must be at most 2000 characters'),
}).required();
const labelSx = { fontSize: '.65rem', textTransform: 'uppercase', letterSpacing: .6, fontWeight: 600 };

const FaqDialog: React.FC<FaqDialogProps> = ({ open, onClose, mode, faq, sections, onSave }) => {
  const [saving, setSaving] = React.useState(false);
  const distinctSections = useMemo(() => {
    const set = new Set(sections.filter(Boolean));
    if (faq?.section) set.add(faq.section);
    return Array.from(set).sort();
  }, [sections, faq]);

  const defaultValues = React.useMemo(() => ({
    sectionSelect: faq?.section || (distinctSections[0] || ''),
    newSection: '',
    question: faq?.question || '',
    answer: faq?.answer || '',
  }), [faq, distinctSections]);

  const { control, handleSubmit, watch, reset, formState: { errors, isSubmitting } } = useForm<FaqDialogForm>({
    resolver: yupResolver(schema) as any,
    defaultValues,
  });

  React.useEffect(() => {
    if (open) {
      reset(defaultValues);
    }
  }, [open, faq, defaultValues, reset]);

  const sectionSelect = watch('sectionSelect');

  const submit: (data: FaqDialogForm) => void = (data) => {
    setSaving(true);
    let section = data.sectionSelect;
    if (section === '__new') {
      section = (data.newSection || '').trim();
    }
    Promise.resolve(onSave({ section, question: data.question.trim(), answer: data.answer.trim() }))
      .finally(() => {
        setSaving(false);
        onClose();
      });
  };

  const title = mode === 'add' ? 'Add FAQ' : 'Edit FAQ';

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        // Prevent closing on backdrop click or Escape key
        if (reason === "backdropClick" || reason === "escapeKeyDown") {
          return;
        }
        onClose();
      }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Typography variant="h6" fontWeight={800} letterSpacing={.5}>{title}</Typography>
      </DialogTitle>
      <form onSubmit={handleSubmit(submit)}>
        <DialogContent dividers sx={{ position: 'relative' }}>
          {/* Loader overlay for saving */}
          {saving && (
            <Box sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              bgcolor: 'rgba(255,255,255,0.7)',
              zIndex: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <Typography sx={{ mr: 2, fontWeight: 600, color: 'primary.main' }}>Saving...</Typography>
              <Box><span className="MuiCircularProgress-root MuiCircularProgress-colorPrimary"><svg className="MuiCircularProgress-svg" viewBox="22 22 44 44"><circle className="MuiCircularProgress-circle" cx="44" cy="44" r="20.2" fill="none" strokeWidth="3.6"></circle></svg></span></Box>
            </Box>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, opacity: saving ? 0.5 : 1, pointerEvents: saving ? 'none' : 'auto' }}>
            {/* Section Select */}
            <Controller
              name="sectionSelect"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth error={!!errors.sectionSelect} required>
                  <InputLabel sx={labelSx}>Section </InputLabel>
                  <Select {...field} label="Section" size="small" MenuProps={{ PaperProps: { sx: { maxHeight: 300 } } }}>
                    {distinctSections.map(sec => (
                      <MenuItem value={sec} key={sec}>{sec}</MenuItem>
                    ))}
                    <MenuItem value="__new" sx={{ fontStyle: 'italic' }}>+ Add New Section</MenuItem>
                  </Select>
                  {errors.sectionSelect && <FormHelperText>{errors.sectionSelect.message}</FormHelperText>}
                </FormControl>
              )}
            />
            {sectionSelect === '__new' && (
              <Controller
                name="newSection"
                control={control}
                render={({ field }) => (
                  <TextField {...field} label="New Section Name" size="small" error={!!errors.newSection} helperText={errors.newSection?.message} InputLabelProps={{ sx: labelSx }} required />
                )}
              />
            )}

            {/* Question */}
            <Controller
              name="question"
              control={control}
              render={({ field }) => (
                <TextField {...field} label="Question" multiline minRows={2} error={!!errors.question} helperText={errors.question?.message} InputLabelProps={{ sx: labelSx }} required />
              )}
            />

            {/* Answer */}
            <Controller
              name="answer"
              control={control}
              render={({ field }) => (
                <TextField {...field} label="Answer" multiline minRows={3} error={!!errors.answer} helperText={errors.answer?.message} InputLabelProps={{ sx: labelSx }} required />
              )}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={onClose} variant="text" sx={{ fontWeight: 600, textTransform: 'none' }} disabled={saving}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={isSubmitting || saving} sx={{ fontWeight: 600, textTransform: 'none', bgcolor: 'secondary.main', '&:hover': { bgcolor: 'primary.main' } }}>{mode === 'add' ? 'Add FAQ' : 'Update FAQ'}</Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default FaqDialog;
